import React, { useState, useEffect, useMemo } from 'react'

import { Modal, Table, Select, DatePicker, InputNumber, Input, Button, message } from 'antd'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'

import { handleApiError } from '@utils/apiErrorHandler'

/**
 * 從採購單建立入庫單的 Modal
 *
 * @param {boolean} open - 是否顯示 Modal
 * @param {Function} onClose - 關閉 Modal 的回調函數
 * @param {Object} purchaseOrder - 採購單物件（含 items 陣列）
 * @param {Object} service - purchaseOrderService (admin 或 operator)
 * @param {Function} onSuccess - 成功建立後的回調函數
 * @param {Array} warehouses - 倉庫列表 [{id, name}]
 */
export default function CreateInboundModal({
  open,
  onClose,
  purchaseOrder,
  service,
  onSuccess,
  warehouses = [],
}) {
  const { t } = useTranslation()

  const [selectedWarehouseId, setSelectedWarehouseId] = useState(null)
  const [eta, setEta] = useState(null)
  const [note, setNote] = useState('')
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)

  // 只顯示尚有剩餘數量的項目
  const availableItems = useMemo(() => {
    if (!purchaseOrder?.items) return []
    return purchaseOrder.items.filter(item => item.remaining_quantity > 0)
  }, [purchaseOrder?.items])

  // Modal 開啟時重置狀態
  useEffect(() => {
    if (open) {
      setSelectedWarehouseId(warehouses.length === 1 ? warehouses[0].id : null)
      setEta(null)
      setNote('')
      // 初始化項目：全選、出貨數量預設為剩餘數量
      setItems(
        availableItems.map(item => ({
          ...item,
          selected: true,
          shipmentQuantity: item.remaining_quantity,
        }))
      )
    }
  }, [open, availableItems])

  // 全選/取消全選
  const handleSelectAll = (checked) => {
    setItems(prev => prev.map(item => ({ ...item, selected: checked })))
  }

  // 更新出貨數量
  const handleQuantityChange = (itemId, value) => {
    setItems(prev =>
      prev.map(item =>
        item.id === itemId ? { ...item, shipmentQuantity: value } : item
      )
    )
  }

  const selectedItems = useMemo(() => items.filter(item => item.selected), [items])

  // 驗證出貨數量
  const hasInvalidQuantity = selectedItems.some(
    item => !item.shipmentQuantity || item.shipmentQuantity <= 0 || item.shipmentQuantity > item.remaining_quantity
  )

  const canSubmit =
    selectedWarehouseId &&
    eta &&
    selectedItems.length > 0 &&
    !hasInvalidQuantity

  const handleConfirm = async () => {
    if (!canSubmit) return

    const data = {
      warehouse_id: selectedWarehouseId,
      eta: eta.format('YYYY-MM-DD'),
      note: note,
      items: selectedItems.map(item => ({
        purchase_order_item_id: item.id,
        quantity: item.shipmentQuantity,
      })),
    }

    try {
      setLoading(true)
      const result = await service.createInbound(purchaseOrder.id, data)
      if (result.success) {
        message.success(t('common.submit.success'))
        onSuccess?.()
        onClose?.()
      } else {
        handleApiError(result, { t })
      }
    } catch (error) {
      console.error('Create inbound error:', error)
      message.error(t('common.error.create_failed'))
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    onClose?.()
  }

  const columns = [
    {
      title: t('models.product.sku'),
      dataIndex: 'product_sku',
      width: 140,
    },
    {
      title: t('models.product.name'),
      dataIndex: 'product_name',
      ellipsis: true,
    },
    {
      title: t('models.purchase_order_item.quantity'),
      dataIndex: 'quantity',
      width: 100,
      align: 'right',
    },
    {
      title: t('models.purchase_order_item.remaining_quantity'),
      dataIndex: 'remaining_quantity',
      width: 100,
      align: 'right',
    },
    {
      title: t('shared.purchase_orders.create_inbound.shipment_quantity'),
      dataIndex: 'shipmentQuantity',
      width: 140,
      align: 'right',
      render: (value, record) => (
        <InputNumber
          min={1}
          max={record.remaining_quantity}
          value={value}
          onChange={(val) => handleQuantityChange(record.id, val)}
          disabled={!record.selected}
          size="small"
          style={{ width: '100%' }}
        />
      ),
    },
  ]

  const rowSelection = {
    selectedRowKeys: selectedItems.map(item => item.id),
    onChange: (selectedRowKeys) => {
      setItems(prev =>
        prev.map(item => ({
          ...item,
          selected: selectedRowKeys.includes(item.id),
        }))
      )
    },
    onSelectAll: (selected) => {
      handleSelectAll(selected)
    },
  }

  return (
    <Modal
      centered
      open={open}
      onCancel={handleCancel}
      title={t('shared.purchase_orders.create_inbound.title')}
      width={800}
      destroyOnHidden
      footer={
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <Button onClick={handleCancel} disabled={loading}>
            {t('common.button.cancel')}
          </Button>
          <Button
            type="primary"
            onClick={handleConfirm}
            loading={loading}
            disabled={!canSubmit}
          >
            {t('common.button.confirm')}
          </Button>
        </div>
      }
    >
      <div style={{ padding: '16px 0' }}>
        {/* 倉庫選擇 */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
            {t('models.warehouse.name')} <span style={{ color: '#ff4d4f' }}>*</span>
          </label>
          <Select
            placeholder={t('shared.purchase_orders.create_inbound.select_warehouse')}
            value={selectedWarehouseId}
            onChange={setSelectedWarehouseId}
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="label"
            options={warehouses.map(w => ({
              label: w.name,
              value: w.id,
            }))}
          />
        </div>

        {/* 預計到貨日 */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
            {t('shared.purchase_orders.create_inbound.eta')} <span style={{ color: '#ff4d4f' }}>*</span>
          </label>
          <DatePicker
            value={eta}
            onChange={setEta}
            style={{ width: '100%' }}
            format="YYYY-MM-DD"
            disabledDate={(current) => current && current.isBefore(dayjs(), 'day')}
          />
        </div>

        {/* 備註 */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
            {t('common.note')}
          </label>
          <Input.TextArea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder={t('shared.purchase_orders.create_inbound.note_placeholder')}
          />
        </div>

        {/* 項目表格 */}
        <div style={{ marginBottom: '8px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
            {t('shared.purchase_orders.create_inbound.select_items')} <span style={{ color: '#ff4d4f' }}>*</span>
          </label>
        </div>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={items}
          rowSelection={rowSelection}
          pagination={false}
          size="small"
          scroll={{ y: 300 }}
        />
      </div>
    </Modal>
  )
}
